import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError } from './api.ts'

interface AsyncState<T> {
  data: T | null
  error: string | null
  loading: boolean
  /** Znovu načte data — po zápisu, který mohl výsledek změnit. */
  reload: () => void
}

/**
 * Načte data při prvním vykreslení a při změně `deps`.
 * Výsledek zastaralého požadavku se zahodí, takže rychlé přepínání
 * mezi klienty nepřepíše novější data staršími.
 */
export function useAsync<T>(loader: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  const loaderRef = useRef(loader)
  loaderRef.current = loader

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(null)

    loaderRef
      .current()
      .then((result) => {
        if (active) setData(result)
      })
      .catch((err: unknown) => {
        if (!active) return
        setError(err instanceof ApiError ? err.message : 'Něco se pokazilo')
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  return { data, error, loading, reload }
}

/**
 * Obal pro akce měnící data: hlídá rozdělaný požadavek a chybovou hlášku,
 * aby se to nemuselo psát v každém formuláři znovu.
 */
export function useAction<TArgs extends unknown[], TResult>(
  action: (...args: TArgs) => Promise<TResult>,
): {
  run: (...args: TArgs) => Promise<TResult | null>
  pending: boolean
  error: string | null
  clearError: () => void
} {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const actionRef = useRef(action)
  actionRef.current = action

  const run = useCallback(async (...args: TArgs): Promise<TResult | null> => {
    setPending(true)
    setError(null)
    try {
      return await actionRef.current(...args)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Něco se pokazilo')
      return null
    } finally {
      setPending(false)
    }
  }, [])

  return { run, pending, error, clearError: useCallback(() => setError(null), []) }
}
